/**
 * Fiona FBA-fee card
 *
 * Fiona compares a product's Amazon-measured size and weight with the FBA
 * fee table and suggests the packaging change (or, US only, the price move)
 * that drops it into a cheaper size tier or weight band. She runs daily and
 * on onboarding, one card per product and marketplace that has a saving.
 *
 * Everything shown comes from the card's decision and features:
 * - decision.current/target_tier_display, current_fee/target_fee, savings,
 *   savings_pct, annual_savings (null without sales in the last 90 days), all
 *   in decision.currency, never converted to USD
 * - decision.changes[]: { measure: length | width | height | weight, current,
 *   target, unit, change, change_pct }, only the measures that change
 * - decision.price_change: { current, target } or null
 * - features.Amazon{Longest,Median,Shortest}Side / AmazonWeight in
 *   features.DimensionUnit / WeightUnit
 *
 * Content only: the status, Accept / Reject and follow-ups live in
 * AgentRecommendations/fiona (FionaCard, FionaActions).
 */

import { Box, Typography } from "@mui/material";
import React from "react";

import { AGENT_INFO } from "../../constants";
import {
  formatFionaDate,
  isFollowUpDue,
  isRemeasurementCard,
  statusLabel,
} from "../AgentRecommendations/fiona/fionaRules";

const FIONA_COLOR = AGENT_INFO.fiona.color;

// Amazon's longest, median and shortest side, as Fiona names them
const MEASURE_LABELS = {
  length: "Longest side",
  width: "Median side",
  height: "Shortest side",
  weight: "Unit weight",
};

const UNIT_LABELS = { inches: "in", cm: "cm", lbs: "lb", kg: "kg" };

const isNumber = (value) => value != null && !Number.isNaN(Number(value));

const formatMoney = (value, currency) => {
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "USD",
    }).format(Number(value));
  } catch {
    return `${Number(value).toFixed(2)} ${currency}`;
  }
};

const formatWholeMoney = (value, currency) => {
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "USD",
      maximumFractionDigits: 0,
    }).format(Number(value));
  } catch {
    return `${Math.round(Number(value))} ${currency}`;
  }
};

const formatNumber = (value) =>
  Number(value).toLocaleString("en-US", { maximumFractionDigits: 2 });

const formatSigned = (value, suffix = "") =>
  `${Number(value) > 0 ? "+" : ""}${formatNumber(value)}${suffix}`;

const unitLabel = (unit) => UNIT_LABELS[unit] || unit || "";

// "2027-01-15" → "15 Jan 2027". Built from the parts so the date doesn't
// shift a day in timezones west of UTC.
export const formatEffectiveDate = (isoDate) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(isoDate || "");
  if (!match) return isoDate || "";
  const [, year, month, day] = match.map(Number);
  return new Date(year, month - 1, day).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

// "16.5 × 10 × 0.7 in, 0.5 lb", or null when Amazon's sizes are missing
export const formatAmazonDimensions = (features = {}) => {
  const sides = [
    features.AmazonLongestSide,
    features.AmazonMedianSide,
    features.AmazonShortestSide,
  ];
  const parts = [];
  if (sides.every(isNumber)) {
    parts.push(
      `${sides.map(formatNumber).join(" × ")} ${unitLabel(
        features.DimensionUnit
      )}`.trim()
    );
  }
  if (isNumber(features.AmazonWeight)) {
    parts.push(
      `${formatNumber(features.AmazonWeight)} ${unitLabel(
        features.WeightUnit
      )}`.trim()
    );
  }
  return parts.length > 0 ? parts.join(", ") : null;
};

const MEASURE_FEATURES = {
  length: "AmazonLongestSide",
  width: "AmazonMedianSide",
  height: "AmazonShortestSide",
  weight: "AmazonWeight",
};

// All four measures, before (Amazon's) and after Fiona's suggestion. Only the
// changed ones are in decision.changes; the rest keep Amazon's value (0%).
export const getPackagingMeasures = (decision = {}, features = {}) => {
  const changes = Array.isArray(decision.changes) ? decision.changes : [];
  return Object.entries(MEASURE_FEATURES)
    .map(([measure, feature]) => {
      const change = changes.find((c) => c.measure === measure);
      const before = isNumber(features[feature])
        ? Number(features[feature])
        : change?.current;
      if (!isNumber(before)) return null;
      const after = isNumber(change?.target) ? Number(change.target) : before;
      const changePct = isNumber(change?.change_pct)
        ? Number(change.change_pct)
        : before
        ? ((after - before) / before) * 100
        : 0;
      const unit =
        change?.unit ||
        (measure === "weight" ? features.WeightUnit : features.DimensionUnit);
      return {
        measure,
        label: MEASURE_LABELS[measure],
        before,
        after,
        changePct,
        unit: unitLabel(unit),
        changed: Boolean(change) && after !== before,
      };
    })
    .filter(Boolean);
};

// Packaging and remeasurement cards both compare the four measures
const isPackagingDecision = (decision = {}) =>
  /packaging|remeasure/.test((decision.action || "").toLowerCase()) ||
  (Array.isArray(decision.changes) && decision.changes.length > 0);

// A price card: the price, not the package, moves the product to a cheaper
// fee. Its dimensions don't change, so it never shows the measures table.
const isPriceDecision = (decision = {}) =>
  (decision.action || "").toLowerCase().includes("price") ||
  (Boolean(decision.price_change) &&
    !(Array.isArray(decision.changes) && decision.changes.length > 0));

const round2 = (n) => Math.round(n * 100) / 100;

// How much space the package takes, as a weight: L × W × H ÷ 139 in inches
// and pounds, ÷ 5000 in centimetres and kilograms
const dimensionalWeight = ({ length, width, height }, dimensionUnit) => {
  if (![length, width, height].every(isNumber)) return null;
  const divisor = unitLabel(dimensionUnit) === "cm" ? 5000 : 139;
  return round2((length * width * height) / divisor);
};

// Which weight Amazon bills the fee on: "unit" for small standard-size,
// otherwise whichever of the unit and dimensional weight is higher
const billedOn = (measures, { dimensionUnit, tier } = {}) => {
  const dimensional = dimensionalWeight(measures, dimensionUnit);
  if (!isNumber(measures.weight) || dimensional == null) return null;
  if (/small standard/i.test(tier || "")) return "unit";
  return dimensional > Number(measures.weight) ? "dim" : "unit";
};

// The weight Amazon bills the fee on (see billedOn). Null when a measure is
// missing.
export const billableWeight = (measures, options = {}) => {
  const billed = billedOn(measures, options);
  if (!billed) return null;
  return billed === "dim"
    ? dimensionalWeight(measures, options.dimensionUnit)
    : round2(Number(measures.weight));
};

const BilledTag = () => (
  <Box
    component="span"
    sx={{
      ml: "6px",
      p: "1px 6px",
      borderRadius: "8px",
      fontSize: "10.5px",
      fontWeight: 600,
      backgroundColor: "#e8eaf6",
      color: AGENT_INFO.fiona.color,
    }}
  >
    billed
  </Box>
);

// "Large standard-size · 1.05 lb"
const tierWithWeight = (tier, weight, unit) =>
  isNumber(weight) ? `${tier} · ${formatNumber(weight)} ${unit}` : tier;

// The FBA fee's price band in the US: under $10, $10–$50, over $50
const priceBand = (price) =>
  price < 10 ? "under $10" : price <= 50 ? "$10–$50" : "over $50";

// Before and after values of the four measures, by name
const measureValues = (measures, key) =>
  Object.fromEntries(measures.map((m) => [m.measure, m[key]]));

const FIONA_TINT = "#f5f6fc";
const FIONA_LINE = "#d6daf0";
const OK_INK = "#2e7d32";

// "Lower FBA Fee - Adjust Packaging" → "Adjust Packaging"
const suggestionKind = (decision = {}) =>
  (decision.action || "").split(" - ").slice(1).join(" - ") ||
  decision.action ||
  "";

// A packaging change where every side and the weight move by 10% or less
const isTweak = (measures) =>
  measures.some((m) => m.changed) &&
  measures.every((m) => !m.changed || Math.abs(m.changePct) <= 10 + 1e-9);

// "-1.5 in (-9.1%)", or null when the measure doesn't change
const formatChange = (m) =>
  m.changed
    ? `${formatSigned(Number((m.after - m.before).toFixed(2)))} ${
        m.unit
      } (${formatSigned(m.changePct.toFixed(1), "%")})`
    : null;

const th = {
  backgroundColor: "#f8f9fb",
  color: "#6b7280",
  fontSize: "11.5px",
  fontWeight: 600,
  textAlign: "left",
  padding: "6px 10px",
};
const td = {
  padding: "6px 10px",
  borderTop: "1px solid #f1f3f6",
  color: "#111827",
  whiteSpace: "nowrap",
};

// Amazon's measures against the target, with the size tier underneath
// Columns: Amazon against Fiona's target, or (remeasurement) against our
// own record of the product
// weights: the dimensional weight before and after, and which weight Amazon
// bills on each side ("unit" or "dim"), tagged "billed" in the table
const MeasuresTable = ({
  measures,
  weights,
  currentTier,
  targetTier,
  targetLabel = "Target",
  changeLabel = "Change",
}) => (
  <Box
    sx={{
      overflowX: "auto",
      mb: "12px",
      border: "1px solid #eceff3",
      borderRadius: "6px",
      maxWidth: 560,
    }}
  >
    <Box
      component="table"
      sx={{
        borderCollapse: "collapse",
        width: "100%",
        fontSize: "12.5px",
        lineHeight: "15px",
        fontVariantNumeric: "tabular-nums",
      }}
    >
      <thead>
        <tr>
          <Box component="th" sx={th} />
          <Box component="th" sx={th}>
            Amazon
          </Box>
          <Box component="th" sx={th}>
            {targetLabel}
          </Box>
          <Box component="th" sx={th}>
            {changeLabel}
          </Box>
        </tr>
      </thead>
      <tbody>
        {measures.map((m) => {
          const change = formatChange(m);
          return (
            <tr key={m.measure}>
              <Box component="td" sx={{ ...td, color: "#6b7280" }}>
                {m.label}
              </Box>
              <Box component="td" sx={td}>
                {formatNumber(m.before)} {m.unit}
                {m.measure === "weight" && weights?.billedBefore === "unit" && (
                  <BilledTag />
                )}
              </Box>
              <Box component="td" sx={td}>
                {formatNumber(m.after)} {m.unit}
                {m.measure === "weight" && weights?.billedAfter === "unit" && (
                  <BilledTag />
                )}
              </Box>
              <Box
                component="td"
                sx={{
                  ...td,
                  color: change ? FIONA_COLOR : "#9e9e9e",
                  fontWeight: change ? 600 : 400,
                }}
              >
                {change || "—"}
              </Box>
            </tr>
          );
        })}
        {weights && (
          <tr>
            <Box component="td" sx={{ ...td, color: "#6b7280" }}>
              Dim. weight
            </Box>
            <Box component="td" sx={td}>
              {formatNumber(weights.dimBefore)} {weights.unit}
              {weights.billedBefore === "dim" && <BilledTag />}
            </Box>
            <Box component="td" sx={td}>
              {formatNumber(weights.dimAfter)} {weights.unit}
              {weights.billedAfter === "dim" && <BilledTag />}
            </Box>
            {(() => {
              const diff = round2(weights.dimAfter - weights.dimBefore);
              return (
                <Box
                  component="td"
                  sx={{
                    ...td,
                    color: diff ? FIONA_COLOR : "#9e9e9e",
                    fontWeight: diff ? 600 : 400,
                  }}
                >
                  {diff ? `${formatSigned(diff)} ${weights.unit}` : "—"}
                </Box>
              );
            })()}
          </tr>
        )}
        {currentTier && (
          <tr>
            <Box component="td" sx={{ ...td, color: "#6b7280" }}>
              Size tier · billed
            </Box>
            <Box component="td" sx={td}>
              {currentTier}
            </Box>
            <Box component="td" sx={{ ...td, fontWeight: 600 }}>
              {targetTier || "?"}
            </Box>
            <Box component="td" sx={td} />
          </tr>
        )}
      </tbody>
    </Box>
  </Box>
);

// The saving on the card's collapsed row: "~$1,448/yr", or the per-unit
// saving when the product had no sales in the last 90 days
export const FionaRowSaving = ({ decision = {} }) => {
  const { currency } = decision;
  if (isNumber(decision.annual_savings)) {
    return (
      <Box sx={{ textAlign: "right", m: "0 8px 0 10px", flex: "none" }}>
        <Typography
          sx={{
            fontSize: "19px",
            fontWeight: 700,
            color: OK_INK,
            fontVariantNumeric: "tabular-nums",
            lineHeight: 1.2,
            letterSpacing: "-0.01em",
          }}
        >
          ~{formatWholeMoney(decision.annual_savings, currency)}
          <Box
            component="span"
            sx={{ fontSize: "12px", fontWeight: 500, ml: "2px" }}
          >
            /yr
          </Box>
        </Typography>
        {isNumber(decision.savings) && (
          <Typography sx={{ fontSize: "11px", color: "#757575" }}>
            {formatMoney(decision.savings, currency)}/unit
          </Typography>
        )}
      </Box>
    );
  }
  if (!isNumber(decision.savings)) return null;
  return (
    <Typography
      sx={{
        m: "0 8px 0 10px",
        flex: "none",
        fontSize: "19px",
        fontWeight: 700,
        color: OK_INK,
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {formatMoney(decision.savings, currency)}
      <Box
        component="span"
        sx={{ fontSize: "12px", fontWeight: 500, ml: "2px" }}
      >
        /unit
      </Box>
    </Typography>
  );
};

const InfoIcon = () => (
  <Box
    component="svg"
    viewBox="0 0 24 24"
    aria-hidden="true"
    sx={{ width: 16, height: 16, flex: "none", mt: "1px" }}
  >
    <circle
      cx="12"
      cy="12"
      r="9"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    />
    <path
      d="M12 11v6M12 7.5v.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    />
  </Box>
);

const FionaDecisionCard = ({ fionaData }) => {
  const decision = fionaData?.decision || {};
  const features = fionaData?.features || {};
  const { currency } = decision;
  const priceChange = decision.price_change;
  const isPrice = isPriceDecision(decision);
  const packagingMeasures =
    !isPrice && isPackagingDecision(decision)
      ? getPackagingMeasures(decision, features)
      : [];
  // The measures table already shows Amazon's values
  const amazonDimensions =
    packagingMeasures.length > 0 ? null : formatAmazonDimensions(features);
  const sameTier =
    decision.current_tier_display &&
    decision.current_tier_display === decision.target_tier_display;
  const kind = suggestionKind(decision);
  const isRemeasure = isRemeasurementCard(fionaData);
  const checkInDue = isFollowUpDue(fionaData?.fiona_card);

  // The size tiers with the weight each is billed on
  const amazonMeasures =
    packagingMeasures.length > 0
      ? measureValues(packagingMeasures, "before")
      : {
          length: features.AmazonLongestSide,
          width: features.AmazonMedianSide,
          height: features.AmazonShortestSide,
          weight: features.AmazonWeight,
        };
  const targetMeasures =
    packagingMeasures.length > 0
      ? measureValues(packagingMeasures, "after")
      : amazonMeasures;
  const weightUnit = unitLabel(features.WeightUnit) || "lb";
  const currentTier =
    decision.current_tier_display &&
    tierWithWeight(
      decision.current_tier_display,
      billableWeight(amazonMeasures, {
        dimensionUnit: features.DimensionUnit,
        tier: decision.current_tier_display,
      }),
      weightUnit
    );
  const targetTier =
    decision.target_tier_display &&
    tierWithWeight(
      decision.target_tier_display,
      billableWeight(targetMeasures, {
        dimensionUnit: features.DimensionUnit,
        tier: decision.target_tier_display,
      }),
      weightUnit
    );
  // The dimensional weight on both sides, and which weight is billed
  const dimBefore = dimensionalWeight(amazonMeasures, features.DimensionUnit);
  const dimAfter = dimensionalWeight(targetMeasures, features.DimensionUnit);
  const weights =
    dimBefore != null && dimAfter != null
      ? {
          dimBefore,
          dimAfter,
          unit: weightUnit,
          billedBefore: billedOn(amazonMeasures, {
            dimensionUnit: features.DimensionUnit,
            tier: decision.current_tier_display,
          }),
          billedAfter: billedOn(targetMeasures, {
            dimensionUnit: features.DimensionUnit,
            tier: decision.target_tier_display,
          }),
        }
      : null;
  const showPriceBand =
    (currency || "USD") === "USD" &&
    priceChange &&
    isNumber(priceChange.current) &&
    isNumber(priceChange.target) &&
    priceBand(priceChange.current) !== priceBand(priceChange.target);

  // A card back for its check-in: what was approved, and where it stands
  if (checkInDue) {
    const fionaCard = fionaData.fiona_card;
    const changed = packagingMeasures
      .filter((m) => m.changed)
      .map(
        (m) =>
          `${m.label.toLowerCase()} ${formatNumber(m.before)} → ${formatNumber(
            m.after
          )} ${m.unit}`
      )
      .join(", ");
    const approvedOn = formatFionaDate(fionaCard.status_changed_at);
    const amazonNow = formatAmazonDimensions(features);
    return (
      <Box sx={{ position: "relative" }}>
        <Typography
          sx={{
            fontSize: "12.5px",
            color: "#6b7280",
            fontStyle: "italic",
            lineHeight: 1.55,
            mb: "12px",
            "& b": { fontStyle: "normal", color: "#111827" },
          }}
        >
          {approvedOn ? (
            <>
              On <b>{approvedOn}</b> you approved
            </>
          ) : (
            "You approved"
          )}{" "}
          my suggestion to <b>{kind.toLowerCase()}</b>
          {changed && (
            <>
              {" "}
              (<b>{changed}</b>)
            </>
          )}{" "}
          at <b>{statusLabel(fionaCard.status)}</b>. Where are we now?
        </Typography>
        <Box
          sx={{
            display: "flex",
            gap: "24px",
            flexWrap: "wrap",
            mb: "12px",
            fontSize: "12.5px",
            color: "#6b7280",
          }}
        >
          {amazonNow && (
            <div>
              Amazon still measures
              <Box
                component="b"
                sx={{
                  display: "block",
                  mt: "2px",
                  fontSize: "13.5px",
                  fontWeight: 600,
                  color: "#111827",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {amazonNow}
              </Box>
            </div>
          )}
          {isNumber(decision.annual_savings) && (
            <div>
              Saving at today's fees
              <Box
                component="b"
                sx={{
                  display: "block",
                  mt: "2px",
                  fontSize: "13.5px",
                  fontWeight: 600,
                  color: OK_INK,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                ~{formatWholeMoney(decision.annual_savings, currency)}/yr
              </Box>
            </div>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ position: "relative" }}>
      {isNumber(decision.current_fee) && isNumber(decision.target_fee) && (
        <Typography sx={{ fontSize: "13px", color: "#4b5563", mb: "10px" }}>
          FBA fee {formatMoney(decision.current_fee, currency)} →{" "}
          <Box component="b" sx={{ color: "#111827" }}>
            {formatMoney(decision.target_fee, currency)}
          </Box>{" "}
          per unit
          {isNumber(decision.savings_pct) &&
            ` (−${formatNumber(Math.abs(decision.savings_pct))}%)`}
        </Typography>
      )}

      <Typography
        sx={{
          fontSize: "14px",
          color: "#666",
          fontStyle: "italic",
          lineHeight: 1.5,
          mb: "12px",
        }}
      >
        I Suggest to Lower FBA Fee Using
        <br />
        <Box
          component="b"
          sx={{ fontStyle: "normal", color: "#000", fontSize: "14.5px" }}
        >
          {kind}:
        </Box>{" "}
        {!isRemeasure && isTweak(packagingMeasures) && (
          <Box
            component="span"
            sx={{
              fontStyle: "normal",
              fontSize: "11px",
              fontWeight: 600,
              borderRadius: "10px",
              p: "2px 8px",
              backgroundColor: "#e8eaf6",
              color: FIONA_COLOR,
            }}
          >
            Tweak · every change ≤10%
          </Box>
        )}
      </Typography>

      {!isNumber(decision.annual_savings) && isNumber(decision.savings) && (
        <Typography sx={{ fontSize: "12.5px", color: "#6b7280", mb: "12px" }}>
          No sales in the last 90 days, so there's no yearly saving yet.
        </Typography>
      )}

      {packagingMeasures.length > 0 ? (
        <MeasuresTable
          measures={packagingMeasures}
          weights={weights}
          currentTier={currentTier}
          targetTier={targetTier}
          {...(isRemeasure
            ? { targetLabel: "Our record", changeLabel: "Difference" }
            : {})}
        />
      ) : (
        <Box sx={{ mb: "12px", fontSize: "13px", color: "#4b5563" }}>
          {isPrice &&
            priceChange &&
            isNumber(priceChange.current) &&
            isNumber(priceChange.target) && (
              <Typography sx={{ fontSize: "inherit", color: "inherit" }}>
                Price {formatMoney(priceChange.current, currency)} →{" "}
                <Box component="b" sx={{ color: "#111827" }}>
                  {formatMoney(priceChange.target, currency)}
                </Box>
              </Typography>
            )}
          {showPriceBand && (
            <Typography sx={{ fontSize: "inherit", color: "inherit" }}>
              Fee price band {priceBand(priceChange.current)} →{" "}
              <Box component="b" sx={{ color: "#111827" }}>
                {priceBand(priceChange.target)}
              </Box>
            </Typography>
          )}
          {currentTier && (
            <Typography sx={{ fontSize: "inherit", color: "inherit" }}>
              Size tier{" "}
              {sameTier ? (
                `${currentTier}${isPrice ? " (the size doesn't change)" : ""}`
              ) : (
                <>
                  {currentTier} →{" "}
                  <Box component="b" sx={{ color: "#111827" }}>
                    {targetTier || "?"}
                  </Box>
                </>
              )}
            </Typography>
          )}
          {amazonDimensions && (
            <Typography sx={{ fontSize: "inherit", color: "inherit" }}>
              Amazon measures {amazonDimensions}
            </Typography>
          )}
        </Box>
      )}

      {decision.reasoning && (
        <Box
          sx={{
            display: "flex",
            gap: "8px",
            alignItems: "flex-start",
            p: "9px 12px",
            borderRadius: "8px",
            backgroundColor: FIONA_TINT,
            border: `1px solid ${FIONA_LINE}`,
            fontSize: "12.5px",
            color: "#283593",
            lineHeight: 1.45,
            mb: "14px",
            maxWidth: 560,
            position: "relative",
          }}
        >
          <InfoIcon />
          <span>
            <b>Why:</b> {decision.reasoning}
          </span>
        </Box>
      )}
    </Box>
  );
};

export default FionaDecisionCard;
